import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ContentAdd from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, type GridColDef, type GridSortModel } from '@mui/x-data-grid';

import { clone, setConfig, ucfirst } from '../../core';
import { bizpost } from '../../ajax';
import { ProgressDialog } from '../../components/dialogs';
import { Data } from '../../components/base';
import { formatDate, formatSource } from '../../components/renderers/formats';
import GridToolbar from '../../components/grid/GridToolbar';
import { ServiceWindow } from '../../components/service';
import { membershipconfig } from '../config/membershipconfig';
import moment from 'moment';

const s: any = {};

class MemberSpecial extends Component<any, any> {
  static propTypes: any;
  static defaultProps: any;

  constructor(props: any) {
    super(props);
    this.state = {
      title: props?.service || 'members',
      url: '/biz',
      params: {},
      action: '',
      filter: {},
      loading: false,
      wizmode: 'add',
      record: {},
      data: [],
      recordCount: 0,
      page: 0,
      rowsPerPage: 10,
      pageMode: 'local',
      order: 'desc',
      orderBy: 'dcd',
      refid: null,
      fwopen: false,
      sortModel: [{ field: 'dcd', sort: 'desc' as const }],
      selected: [],
      selectedRecord: null,
      actions: [],
      cols: [],
    };
  }

  getServiceKey = () => {
    const name = String(this.props?.service || '').toLowerCase();
    return ['members', 'students', 'professionals'].includes(name) ? name : 'members';
  };

  configuration = () => {
    const key = this.getServiceKey();
    const cfg: any = membershipconfig as any;
    return cfg[key] || cfg.members;
  };

  getServiceConfig = () => {
    const config = setConfig(Object.assign({}, this.configuration()));
    const serviceConfig = clone(config);
    const serviceData = serviceConfig.service;
    return { svc: serviceConfig, svd: serviceData };
  };

  setPageMode = () => {
    const { svd } = this.getServiceConfig();
    const { pageMode = 'local' } = svd || {};
    this.setState({ pageMode });
  };

  componentDidMount() {
    this.setPageMode();
    this.refresh();
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevProps.service !== this.props.service) {
      this.setPageMode();
      this.setState({ page: 0, selected: [], selectedRecord: null, actions: [] }, () => this.refresh());
      return;
    }
    if (prevState.page !== this.state.page && this.state.pageMode === 'remote') {
      this.refresh();
    }
  }

  refresh = async (inparams?: any) => {
    try {
      const { svd } = this.getServiceConfig();
      const { pageMode = 'local', name: serviceName = this.getServiceKey() } = svd || {};
      const { params, rowsPerPage, page } = this.state;

      this.startLoading();
      const pos = page * rowsPerPage;
      const plm = rowsPerPage;
      const paging = pageMode === 'remote' ? { pos, plm } : {};
      const extraparams = inparams || params;
      const baseparams = {
        s: serviceName,
        a: 'find',
        ...extraparams,
        ...paging,
      };
      const response = await bizpost(baseparams);
      this.setState({ data: response?.sd || [], recordCount: +(response?.rc || 0) });
    } catch (error) {
      this.setState({ data: [], recordCount: 0 });
      const errorx: any = error || {};
      this.props.showNotification(
        errorx.message || 'request failed',
        errorx.type || 'warn',
        errorx.title || 'Loading Error'
      );
    } finally {
      if (this.state.loading) this.stopLoading();
    }
  };

  onSearch = async (_service: string, query: string) => {
    try {
      const { svd } = this.getServiceConfig();
      const serviceName = svd?.name || this.getServiceKey();
      const { params } = this.state;

      this.startLoading();
      const extraparams = { qry: query, ...params };
      const response = await bizpost({ s: serviceName, a: 'search', ...extraparams });
      this.setState({ data: response?.sd || [], recordCount: +(response?.rc || 0) });
    } catch (error) {
      this.setState({ data: [], recordCount: 0 });
      const errorx: any = error || {};
      this.props.showNotification(
        errorx.message || 'request failed',
        errorx.type || 'warn',
        errorx.title || 'Loading Error'
      );
    } finally {
      if (this.state.loading) this.stopLoading();
    }
  };

  startLoading = () => {
    this.setState({ loading: true });
  };

  stopLoading = () => {
    this.setState({ loading: false });
  };

  handleRecord = (record: any) => () => {
    const actions = this.props.actionSet || {};
    const { url } = this.state;
    const action = actions[record.action] || Data.actions[record.action] || 'Process';
    const { handleRecord } = this.props;
    const xrecord = typeof handleRecord === 'function' ? handleRecord(record) : record;
    if (xrecord) {
      this.setState({
        record: xrecord.item,
        wizmode: xrecord.action,
        action,
        url: xrecord.url || url,
        fwopen: true,
      });
    }
  };

  handleClose = () => {
    this.setState({ fwopen: false });
  };

  onResponse = (status: any, _response: any) => {
    if (status) {
      this.handleClose();
      this.refresh();
    }
  };

  formatStatus = (_type: string) => (id: any) => {
    const base = ['disabled', 'active', 'stopped'];
    return base[id] || Data.statuses?.list?.[id] || 'unknown';
  };

  rowActions = (_id: any, rec: any) => {
    const { svd } = this.getServiceConfig();
    const buttons: string[] = svd?.bar?.btn || ['view'];
    const mapped = buttons.map((btn) => (btn === 'del' ? 'delete' : btn));
    if (!mapped.includes('view')) mapped.unshift('view');
    return Array.from(new Set(mapped));
  };

  onPageChange = (_event: any, page: any) => {
    this.setState({ page });
  };

  onRowsPerPageChange = (event: any) => {
    this.setState({ rowsPerPage: event.target.value });
  };

  filterColumns = (rows: any[]) => {
    const { cols } = this.state;
    const colids: any[] = Array.isArray(cols) && cols.length > 0 ? cols : rows.map((row: any) => row.id);
    return rows.filter((row: any) => colids.includes(row.id));
  };

  setColumns = (_event: any, fcols: any) => {
    this.setState({ cols: fcols });
  };

  onSortModelChange = (sortModel: GridSortModel) => {
    this.setState({ sortModel });
  };

  onRowSelect = (_event: any, row: any) => {
    const { data } = this.state;
    const selected = data?.find((d: any) => d.rid === row?.rid);
    if (selected && selected.rid !== undefined) {
      const item = data?.find((d: any) => d.rid === selected.rid);
      const gridactions = this.rowActions(row?.id, item);
      this.setState({
        selected: [selected?.rid],
        selectedRecord: item,
        actions: gridactions,
      });
    }
  };

  showText = (value: any) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    return String(value);
  };

  showDate = (value: any) => {
    if (!value) return 'N/A';
    const dt = formatDate(value);
    return dt === 'Invalid date' ? this.showText(value) : moment(dt).format('LL');
  };

  showName = (record: any) => {
    const parts = [record?.ttl, record?.fnm, record?.otn, record?.lnm].filter((v: any) => !!v);
    return parts.length > 0 ? parts.join(' ') : 'N/A';
  };

  initials = (name: string) => {
    const parts = name
      .split(' ')
      .map((n) => n.trim())
      .filter((n) => !!n);
    const first = parts[0]?.[0] || '';
    const second = parts[1]?.[0] || '';
    return `${first}${second}`.toUpperCase() || 'M';
  };

  renderDetailItem = (label: string, value: any) => {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2">{this.showText(value)}</Typography>
      </Box>
    );
  };

  renderViewDialog = () => {
    const { fwopen, wizmode, record } = this.state;
    if (!(fwopen && wizmode === 'view')) return null;

    const fullName = this.showName(record);
    const imageUrl = record?.img || '';

    return (
      <Dialog open={fwopen} onClose={this.handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Member Details</Typography>
            <IconButton onClick={this.handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, gap: 3 }}>
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #e6e6e6', textAlign: 'center' }}>
              <Avatar
                src={imageUrl}
                alt={fullName}
                sx={{ width: 150, height: 150, mx: 'auto', mb: 1.5, bgcolor: 'primary.main', fontSize: 44 }}
              >
                {this.initials(fullName)}
              </Avatar>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {this.showText(record?.shc)}
              </Typography>
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label={`Status: ${this.formatStatus('view')(record?.sts)}`} color="primary" variant="outlined" />
                <Chip size="small" label={`${this.showText(record?.gen)}`} variant="outlined" />
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personal
                </Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {this.renderDetailItem('Date of Birth', this.showDate(record?.ddb))}
                  {this.renderDetailItem('Place of Birth', record?.pob)}
                  {this.renderDetailItem('Country', record?.ren || record?.ssc)}
                  {this.renderDetailItem('Hometown', record?.hmt)}
                  {this.renderDetailItem('Home District', record?.hdn)}
                  {this.renderDetailItem('Home Region', record?.hrn)}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Contact
                </Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {this.renderDetailItem('Email', record?.ema)}
                  {this.renderDetailItem('Contact', record?.con)}
                  {this.renderDetailItem('Secondary Contact', record?.ccn)}
                  {this.renderDetailItem('Residential Address', record?.rad)}
                  {this.renderDetailItem('Postal Address', record?.rpa)}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Family And Church
                </Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {this.renderDetailItem('Parent Name', record?.prn || record?.pmn)}
                  {this.renderDetailItem('Guardian', record?.gun)}
                  {this.renderDetailItem('Guardian Contact', record?.gcn)}
                  {this.renderDetailItem('Marital Status', record?.mrc)}
                  {this.renderDetailItem('Spouse Name', record?.spn || record?.smn)}
                  {this.renderDetailItem('Spouse Contact', record?.spc)}
                  {this.renderDetailItem('Occupation Status', record?.orn)}
                  {this.renderDetailItem('Occupation', record?.ocd)}
                  {this.renderDetailItem('Day Group', record?.gnm)}
                  {this.renderDetailItem('Membership Date', this.showDate(record?.tmd))}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Record
                </Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {/* {this.renderDetailItem('Record ID', record?.rid)}
                  {this.renderDetailItem('User', record?.usn)} */}
                  {this.renderDetailItem('Created', this.showDate(record?.dcd))}
                  {this.renderDetailItem('Updated', this.showDate(record?.stp))}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { code, showNotification } = this.props;
    const { svc: serviceConfig, svd: serviceData } = this.getServiceConfig();
    const {
      url,
      action,
      loading,
      wizmode,
      fwopen,
      record,
      data,
      recordCount,
      page,
      rowsPerPage,
      order,
      orderBy,
      sortModel,
      selected,
      selectedRecord,
      actions,
    } = this.state;

    if (!serviceData) {
      this.props.setHome();
      return null;
    }

    const cancreate = serviceConfig.create === true;
    const serviceName = serviceData.name || this.getServiceKey();
    const fabstyle = { position: 'fixed', right: 20, bottom: 20 };
    const isform = action !== '';
    const stitle = serviceData.title || ucfirst(serviceName);
    const cm = serviceData.cols;

    const fmformatters = {
      sts: this.formatStatus('form'),
      ddb: formatDate,
      mad: formatDate,
      tmd: formatDate,
      dcd: formatDate,
      stp: formatDate,
      src: formatSource,
    };
    const gdformatters = {
      sts: this.formatStatus('grid'),
      ddb: formatDate,
      mad: formatDate,
      tmd: formatDate,
      dcd: formatDate,
      stp: formatDate,
      src: formatSource,
    };

    const cformatters = serviceConfig.formatters || {};
    const { form: cfformatters, grid: cgformatters } = cformatters;
    const _formformatters = Object.assign({}, fmformatters, cfformatters, {});
    const _gridformatters = Object.assign({}, gdformatters, cgformatters, {});
    const _rowActions = this.rowActions;
    const newrecord = { item: { sts: 1, img: '', a: 'add' }, action: 'add' };
    const { pageMode = 'local' } = serviceData;
    const panels = serviceConfig.panels;
    const isRemote = pageMode === 'remote';
    const rowCount = isRemote ? recordCount : data?.length || 0;
    const frows = this.filterColumns(cm);
    const columns: GridColDef[] = frows.map((col: any) => ({
      field: col.id,
      headerName: col.label || col.id,
      width: Math.min(col.width || 170, col.maxWidth || 420),
      minWidth: col.minWidth || 110,
      maxWidth: col.maxWidth || 420,
      sortable: true,
      align: col.numeric ? 'right' : 'left',
      headerAlign: col.numeric ? 'right' : 'left',
      renderCell: (params: any) => {
        const value = params.value;
        const row = params.row;
        return _gridformatters[col.id]
          ? _gridformatters[col.id](value, row)
          : value !== null && value !== undefined
          ? String(value)
          : '';
      },
    }));

    return (
      <Box>
        {cancreate && (
          <Fab
            color="secondary"
            sx={{
              ...s.fixedfab,
              ...fabstyle,
            }}
            onClick={this.handleRecord(newrecord)}
          >
            <ContentAdd sx={{ ...s.actionfab }} />
          </Fab>
        )}

        {isform && fwopen && wizmode !== 'view' && (
          <ServiceWindow
            open={fwopen}
            mode={wizmode}
            code={code}
            title={`${action} ${stitle}`}
            service={serviceName}
            url={url}
            panels={panels}
            actions={_rowActions}
            actionSet={this.props.actionSet}
            rowActions={this.props.rowActions}
            record={record}
            data={data}
            sdata={serviceData}
            sconfig={serviceConfig}
            formatters={_formformatters}
            onChange={this.props.onChange}
            refresh={this.refresh}
            onSubmit={this.props.onSubmit}
            onResponse={this.onResponse}
            onClose={this.handleClose}
            showNotification={showNotification}
          />
        )}

        {this.renderViewDialog()}

        {loading && (
          <ProgressDialog
            open={loading}
            loading={loading}
            startLoading={this.startLoading}
            stopLoading={this.stopLoading}
          />
        )}

        {!fwopen && (
          <Paper sx={{ width: '100%' }}>
            <GridToolbar
              title={stitle}
              sdata={serviceData}
              actions={actions}
              numSelected={selected.length}
              selectedRecord={selectedRecord}
              setColumns={this.setColumns}
              cols={cm}
              fcols={frows}
              handleSelect={this.handleRecord.bind(this)}
              onAction={this.handleRecord.bind(this)}
              refresh={() => this.refresh()}
            />
            <Box sx={{ width: '100%', height: 600 }}>
              <DataGrid
                rows={data || []}
                columns={columns}
                getRowId={(row) => {
                  if (row.id !== undefined && row.id !== null) return row.id;
                  if (row.rid !== undefined && row.rid !== null) return row.rid;
                  const index = (data || []).indexOf(row);
                  return index >= 0 ? `row-${index}` : `row-${Math.random()}`;
                }}
                rowCount={rowCount}
                paginationModel={{ page, pageSize: rowsPerPage }}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                paginationMode={isRemote ? 'server' : 'client'}
                sortingMode={isRemote ? 'server' : 'client'}
                sortModel={sortModel}
                onSortModelChange={this.onSortModelChange}
                onPaginationModelChange={(model) => {
                  this.onPageChange(null, model.page);
                  if (model.pageSize !== rowsPerPage) this.onRowsPerPageChange({ target: { value: model.pageSize } });
                }}
                onRowClick={(e) => this.onRowSelect(e, e.row)}
                checkboxSelection
                disableMultipleRowSelection
                disableRowSelectionOnClick={false}
                hideFooterSelectedRowCount
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  '& .MuiDataGrid-main': {
                    overflowX: 'hidden',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflowX: 'auto',
                  },
                  '& .MuiDataGrid-cell': {
                    py: 1,
                  },
                }}
              />
            </Box>
          </Paper>
        )}
      </Box>
    );
  }
}

MemberSpecial.propTypes = {
  service: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  setPage: PropTypes.func,
  setHome: PropTypes.func,
  showNotification: PropTypes.func,
};

export default MemberSpecial;
